import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

// Warehouse studio hero image (white placeholder rectangle removed from source)
const HERO_IMAGE = "/hero-clean.jpg";

// Subtle "shop the look" hotspots positioned over the warehouse hoodies.
const HOTSPOTS = [
  { x: 13, y: 65, slug: "tdc-porsche-911-hoodie", label: "TDC Porsche 911 Hoodie" },
  { x: 37, y: 55, slug: "tdc-classic-hoodie-black", label: "TDC Classic Hoodie (Black)" },
  { x: 70, y: 55, slug: "tdc-dont-tell-mama-hoodie-black", label: "TDC Don't Tell Mama (Black)" },
  { x: 89, y: 66, slug: "tdc-classic-hoodie-white", label: "TDC Classic Hoodie (White)" },
];

const scrollToCollections = () => {
  const target = document.getElementById("shop-collection");
  if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  else window.location.assign("/collections");
};

const HeroAnimation = () => {
  return (
    <section
      className="relative w-full bg-black overflow-hidden"
      data-testid="hero"
    >
      {/* Mobile: exact image aspect ratio so the FULL warehouse shot is visible
          (same framing as desktop). Desktop: full-viewport hero as before.
          No viewport-height dependence on mobile => scrolling can never resize/zoom it. */}
      <div className="relative w-full aspect-[1478/825] md:aspect-auto md:h-[100svh] md:min-h-[560px] overflow-hidden">
        {/* Warehouse background image — slow smooth CSS zoom, unaffected by scroll */}
        <img
          src={HERO_IMAGE}
          alt="The Dripchexk warehouse — front and back hoodie showcase"
          className="hero-zoom absolute inset-0 w-full h-full object-cover select-none"
          data-testid="hero-image"
          draggable={false}
        />

        {/* Subtle dark overlay for legibility */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/70 via-black/25 to-black/70" />

        {/* Tiny brand mark — top left */}
        <p
          className="absolute top-3 left-3 sm:top-8 sm:left-10 z-20 text-white text-[9px] sm:text-xs tracking-[0.35em] uppercase font-semibold"
          data-testid="hero-brand-mark"
        >
          THE DRIP CHEXK.
        </p>

        {/* Small drop tag — top right */}
        <p className="absolute top-3 right-3 sm:top-8 sm:right-10 z-20 text-white/70 text-[8px] sm:text-[11px] tracking-[0.3em] uppercase">
          Drop 01 / Live
        </p>

        {/* Subtle shop-the-look hotspots (hidden on small screens) */}
        <div className="hidden md:block absolute inset-0 z-[15]" data-testid="hotspot-layer">
          {HOTSPOTS.map((h) => (
            <Link
              key={h.slug}
              to={`/product/${h.slug}`}
              aria-label={`Shop ${h.label}`}
              data-testid={`hotspot-${h.slug}`}
              className="group absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${h.x}%`, top: `${h.y}%` }}
            >
              <span className="absolute inset-0 rounded-full bg-white/30 blur-[2px] animate-ping opacity-40" style={{ width: 14, height: 14, left: -7, top: -7 }} />
              <span className="relative block w-2.5 h-2.5 rounded-full bg-white/70 group-hover:bg-[#FF69B4] transition-colors ring-1 ring-white/40 group-hover:ring-[#FF69B4]/70" />
              <span className="absolute left-4 top-1/2 -translate-y-1/2 whitespace-nowrap bg-black/80 backdrop-blur px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {h.label} →
              </span>
            </Link>
          ))}
        </div>

        {/* EXPLORE button — compact. The white rectangle was erased from the
            background image itself, so the button no longer needs to cover it. */}
        <button
          onClick={scrollToCollections}
          data-testid="explore-cta"
          aria-label="Explore the drop"
          className="group absolute left-1/2 -translate-x-1/2 bottom-[28%] md:bottom-[32%] z-20 bg-white hover:bg-[#FF69B4] transition-colors duration-300 w-[32vw] max-w-[240px] sm:w-[220px] py-2 sm:py-5 shadow-[0_0_40px_rgba(255,255,255,0.12)]"
        >
          <span className="block text-black group-hover:text-white text-[10px] sm:text-sm tracking-[0.4em] uppercase font-bold transition-colors">
            Explore
          </span>
        </button>

        {/* Faint scroll hint bottom — desktop only (mobile hero is compact) */}
        <div className="hidden md:block absolute bottom-6 left-1/2 -translate-x-1/2 z-20 text-white/50 text-[10px] tracking-[0.3em] uppercase">
          Scroll ↓
        </div>
      </div>

      {/* Marquee strip below hero */}
      <div className="relative border-y border-white/10 overflow-hidden bg-black">
        <motion.div
          className="flex whitespace-nowrap py-3 text-[10px] tracking-[0.3em] uppercase text-white/50"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 32, repeat: Infinity, ease: "linear" }}
        >
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex shrink-0">
              {[
                "NEW DROP",
                "DRIPCHEXK",
                "BUILDING A LEGACY",
                "MADE IN INDIA",
                "WORN WORLDWIDE",
                "FREE SHIP ₹1,999+",
                "COD AVAILABLE",
                "5★ REVIEWED",
              ].map((w) => (
                <span key={w} className="px-6 flex items-center gap-6">
                  {w} <span className="text-[#FF69B4]/70">✦</span>
                </span>
              ))}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default HeroAnimation;
